import { createSelector } from 'reselect';
import assert from 'assert';

// Selectors
import {
  makeSelectedSelector,
  getVehicleProperty,
  getServicePoint,
  getServices,
  getTransportationOption
} from 'store/reducers/appointment/selectors';
import { getQueryParam } from 'store/reducers/queryParams/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import {
  getAppFlow,
  getRecaptchaToken as getRecaptchaTokenUI
} from 'store/reducers/userInteractions/selectors';
import {
  getIsObfuscated,
  getObfuscatedBy
} from 'store/reducers/appointment/contactInfo/selectors';

// Utils
import { LIMIT_TEN_CHARACTERS } from 'utils/regex';
import { isInsideIFrame, getMetaTagContentByName } from 'utils/DOM';
import { isTrue, undefinedToTrue } from 'utils/varValidation';

// Constants
import { VEHICLE_TRIM_DATA, VEHICLE_VALUES } from 'constants/vehicle';
import { SERVICES_TYPE } from 'constants/services';
import { SEARCH_CRITERIA_FIND_ME } from 'constants/findMe';
import { APP_FLOW } from 'constants/appFlow';
import { TRANSPORTATION } from 'constants/transportation';

const { PHONE, EMAIL, LICENSE_NUMBER } = SEARCH_CRITERIA_FIND_ME;
const { QUICK_APPT } = APP_FLOW;

const { VALET, VALETLOANER, LOANER } = TRANSPORTATION;

export const dealerSettingsSelector = state => state.dealerSettings;

export const getOther = createSelector(
  dealerSettingsSelector,
  dealerSettings => {
    if (dealerSettings && dealerSettings.makes) {
      const { OTHER } = VEHICLE_VALUES;
      return dealerSettings.makes.find(
        make => make.make.toUpperCase() === OTHER
      );
    } else {
      return null;
    }
  }
);

export const amenities = createSelector(
  dealerSettingsSelector,
  dealerSettings => {
    if (dealerSettings) {
      return dealerSettings.amenities;
    } else {
      return [];
    }
  }
);

export const makes = createSelector(
  dealerSettingsSelector,
  getOther,
  (dealerSettings, other) => {
    if (dealerSettings && dealerSettings.makes) {
      const { OTHER } = VEHICLE_VALUES;
      const makeList = dealerSettings.makes.filter(
        make => make.make.toUpperCase() !== OTHER
      );
      if (other) {
        makeList.push(other);
      }
      return makeList.map(make => {
        return { ...make, label: make.make, data: make.make };
      });
    } else {
      return [];
    }
  }
);

export const getMakeConfig = createSelector(
  dealerSettingsSelector,
  makeSelectedSelector,
  (dealerSettings, selectedMake) => {
    if (dealerSettings && dealerSettings.makes) {
      return dealerSettings.makes.filter(make => make.make === selectedMake)[0];
    } else {
      return {};
    }
  }
);

export const findMake = createSelector(
  dealerSettingsSelector,
  dealerSettings => make => {
    if (dealerSettings && make) {
      return dealerSettings.makes.find(
        m => m.make.toUpperCase() === make.toUpperCase()
      );
    }
    return null;
  }
);

export const getDefaultDrivingConditions = createSelector(
  getMakeConfig,
  makeConfig => {
    return makeConfig ? makeConfig.defaultDrivingConditions : false;
  }
);

export const getCappedPricing = createSelector(getMakeConfig, makeConfig => {
  return makeConfig ? Boolean(makeConfig.cappedPricing) : false;
});

export const getVariant = createSelector(getMakeConfig, makeConfig => {
  return makeConfig ? makeConfig.variant : false;
});

export const getExpandedServicesView = createSelector(
  getMakeConfig,
  ({ expandedMenuServicesView }) => expandedMenuServicesView
);

export const getShowMileage = createSelector(getMakeConfig, makeConfig => {
  return makeConfig ? makeConfig.showMileage : false;
});

export const getShowRegDate = createSelector(
  getMakeConfig,
  makeConfig =>
    makeConfig && (makeConfig.cappedPricing || makeConfig.timeBasedMenus)
);

export const getShowLastLane = createSelector(
  getShowMileage,
  getShowRegDate,
  getAppFlow,
  (showMileage, showRegDate, appFlow) => {
    return (
      appFlow !== QUICK_APPT && (undefinedToTrue(showMileage) || showRegDate)
    );
  }
);

export const policies = createSelector(
  dealerSettingsSelector,
  dealerSettings => {
    if (dealerSettings) {
      return { ...dealerSettings.policies };
    } else {
      return {};
    }
  }
);

export const getDealerName = createSelector(
  policies,
  makes,
  getQueryParam('variant'),
  (policies, makes, variant) => {
    const make = makes.find(
      make => make.variant === variant && make.dealerName
    );
    return make ? make.dealerName : policies.name;
  }
);

export const getPolicyProperty = createSelector(
  policies,
  policies => property => policies[property]
);

export const getTellusmoreSvcId = createSelector(
  policies,
  policies => +policies['tellusmoreSvcId']
);

// TODO(mauricio): map properties to valid values in the dealerSettings type
/**
 * Gets the value of a policy, the signature follows the reselect guide
 * on selectors with props (props is a single arg, we don't need to
 * define an object `props`)
 *
 * @example
 * getProperty(state, { property: 'key' })
 */
export function getProperty(
  state: State,
  key: $Keys<Settings>
): $Values<Settings> {
  const dealerSettings = dealerSettingsSelector(state);
  return dealerSettings.policies[key];
}

export const getDealerContact = createSelector(
  policies,
  policies => policies.dealerContact
);

export const getDealerCountryCode = createSelector(
  getDealerContact,
  dealerContact => {
    if (dealerContact) {
      return dealerContact.country;
    } else {
      return 'US';
    }
  }
);

export const getSupportedCountries = createSelector(
  policies,
  getDealerContact,
  (policies, dealerContact) => {
    if (policies.supportedCountries && policies.supportedCountries.length > 0) {
      return policies.supportedCountries.map(country => {
        return { value: country.code, label: country.name };
      });
    } else if (dealerContact) {
      return [{ value: dealerContact.country, label: dealerContact.country }];
    } else {
      return [];
    }
  }
);

export const defaultMakeSelection = createSelector(policies, policies => {
  if (policies) {
    return policies.defaultMakeSelection;
  } else {
    return null;
  }
});

export const getMileageUnit = createSelector(
  policies,
  getQueryParam('units'),
  ({ mileageUnit }, qpMileageUnits) => mileageUnit || qpMileageUnits
);

export const getPriceVisibility = createSelector(
  dealerSettingsSelector,
  getPolicyProperty,
  getVehicleProperty,
  makeSelectedSelector,
  (dealerSettings, getPolicyPropertyFn, getVehiclePropertyFn, make) => type => {
    if (!dealerSettings) {
      // remove after call to settings has been implemented
      return true;
    }
    const { priceVisibility } = dealerSettings;
    const { MENU, ALACARTE } = SERVICES_TYPE;
    assert(
      !type || type === MENU || type === ALACARTE,
      'Service type has an invalid value.'
    );
    const iDontKnowSelected = () => {
      let i;
      for (i = 0; i < VEHICLE_TRIM_DATA.length; i += 1) {
        if (getVehiclePropertyFn(VEHICLE_TRIM_DATA[i])) {
          return false;
        }
      }
      return getPolicyPropertyFn('dealerShowPricingWhenIdk') === '0';
    };
    let pvtemp;
    const { OTHER, ANY } = VEHICLE_VALUES;
    if (make && (!iDontKnowSelected() || make === OTHER)) {
      pvtemp = priceVisibility[make];
      if (!pvtemp || make === OTHER) {
        pvtemp = priceVisibility[ANY];
      }
      if (pvtemp) {
        return (
          pvtemp['showPricingConsumer'] &&
          pvtemp[`showPricingConsumer${type || ''}`]
        );
      }
    }
    return false;
  }
);

export const getSymptomSurveyEnabled = createSelector(policies, policies => {
  if (policies['symptomSurveyEnabled']) {
    return policies['symptomSurveyEnabled'];
  } else {
    return null;
  }
});

export const getLanguagesAvailable = createSelector(policies, policies => {
  if (policies['locales']) {
    return policies['locales'];
  } else {
    return [];
  }
});

export const languagesAvailableGreaterThanOne = createSelector(
  getLanguagesAvailable,
  languagesAvailable => {
    if (languagesAvailable) {
      return languagesAvailable.length > 1;
    }
  }
);

export const getDealerLocaleName = createSelector(
  getLocaleCode,
  getLanguagesAvailable,
  (localeCode, languagesAvailable) => {
    if (languagesAvailable.length > 0) {
      const languageSelected = languagesAvailable.find(
        language => localeCode === language.localeCode
      );
      if (languageSelected) {
        return languageSelected.localeName;
      }
    }
    return '';
  }
);

export const getMarketingSMSConfiguration = createSelector(
  policies,
  policies => {
    if (policies['marketingSMSConfiguration']) {
      return policies['marketingSMSConfiguration'];
    } else {
      return null;
    }
  }
);

export const isRegistrationNumEnabled = createSelector(
  policies,
  policies => policies['consumerInfoRegNumNeeded'] || false
);

export const searchCriteriaList = createSelector(
  isRegistrationNumEnabled,
  isRegistrationNumEnabled => [
    PHONE,
    EMAIL,
    ...(isRegistrationNumEnabled ? [LICENSE_NUMBER] : [])
  ]
);

export const isLicenseNumberValid = createSelector(
  isRegistrationNumEnabled,
  getVehicleProperty,
  getIsObfuscated,
  getObfuscatedBy,
  (regNumberEnabled, getVehiclePropertyFn, isObfuscated, obfuscatedBy) => {
    const licenseNumber = getVehiclePropertyFn('licenseNumber');
    let valid = true;
    if (!isObfuscated) {
      valid =
        !regNumberEnabled ||
        (licenseNumber && LIMIT_TEN_CHARACTERS.test(licenseNumber));
    }
    return valid;
  }
);

export const showHeader = createSelector(
  getQueryParam('showDealerName'),
  showDealerName => {
    return (
      showDealerName !== 'false' &&
      (!isInsideIFrame() || isTrue(showDealerName))
    );
  }
);

export const getFlexPayConfig = createSelector(
  policies,
  policies => policies.flexPay
);

export const getFlexPayConfiguration = createSelector(
  getFlexPayConfig,
  flexPay => Boolean(flexPay && flexPay.enable)
);

export const getFlexPayProviders = createSelector(
  getFlexPayConfig,
  flexPay => (flexPay ? flexPay.providers : [])
);

export const getExternalValetProvider = createSelector(
  policies,
  policies => policies.thirdPartyValet
);

export const getExternalLoanerProvider = createSelector(
  policies,
  policies => policies.thirdPartyLoaner
);

export const getDisplayPackages = createSelector(
  policies,
  getServicePoint,
  (policies, servicePoint) => {
    const hasPackages =
      servicePoint && servicePoint.menuType.services.length > 0;
    return isTrue(policies.recommendationAllowed) || hasPackages;
  }
);

export const getConsumerAllowAnyGreeterSel = createSelector(
  policies,
  policies => isTrue(policies['consumerAllowAnyGreeterSel'])
);

export const getTellUsMoreSvcVisible = createSelector(policies, policies =>
  isTrue(policies['tellusmoreSvcVisible'])
);

export const getHideProfile = createSelector(
  policies,
  policies => policies['hideProfile'] || false
);

export const getQuickAppointment = createSelector(
  policies,
  policies => policies['quickAppointment'] || false
);

export const getTellUsMoreNotes = createSelector(
  getServices,
  getTellusmoreSvcId,
  (services, tellUsMoreSvcId) => {
    const findCallback = ({ id }) => id === tellUsMoreSvcId;
    const tellUsMoreService = services.find(findCallback);
    const { notes, name } = tellUsMoreService
      ? tellUsMoreService
      : { notes: '', name: '' };
    return notes || name;
  }
);

export const isValetOrLoanerIntegration = createSelector(
  getTransportationOption,
  getExternalValetProvider,
  getExternalLoanerProvider,
  (transportationOption, externalValetProvider, externalLoanerProvider) => {
    const isValet =
      transportationOption === VALET || transportationOption === VALETLOANER;
    return (
      (isValet && externalValetProvider) ||
      (transportationOption === LOANER && externalLoanerProvider)
    );
  }
);

export const getRecaptchaConfig = createSelector(policies, policies => {
  const siteKey = getMetaTagContentByName('xtime:recaptcha-key');
  return siteKey && policies.recaptchaCfg;
});

export const getNoGToken = createSelector(getRecaptchaConfig, recaptchaCfg => {
  const dealerType = getMetaTagContentByName('xtime:dealerType');
  const isTD = dealerType === 'td';
  return recaptchaCfg && recaptchaCfg.noToken && isTD;
});

export const getRecaptchaToken = createSelector(
  getNoGToken,
  getRecaptchaTokenUI,
  (noToken, recaptchaToken) => (noToken ? null : recaptchaToken)
);

export const isRecaptchaEnabledOnFindMe = createSelector(
  getRecaptchaConfig,
  recaptchaCfg => recaptchaCfg && recaptchaCfg.findMe
);

export const isRecaptchaEnabledOnCancelAppt = createSelector(
  getRecaptchaConfig,
  recaptchaCfg => recaptchaCfg && recaptchaCfg.cancelAppt
);

export const isRecaptchaEnabledOnConfirmAppt = createSelector(
  getRecaptchaConfig,
  recaptchaCfg => recaptchaCfg && recaptchaCfg.confirmAppt
);

export const isRecaptchaEnabledOnRescheduleAppt = createSelector(
  getRecaptchaConfig,
  recaptchaCfg => recaptchaCfg && recaptchaCfg.rescheduleAppt
);

export const isRecaptchaEnabledOnRegisterUser = createSelector(
  getRecaptchaConfig,
  recaptchaCfg => recaptchaCfg && recaptchaCfg.registerUser
);

export const getSmsSingleOptIn = createSelector(
  policies,
  policies => policies['smsSingleOptIn'] || false
);

export const getOrgKey = createSelector(
  policies,
  policies => policies['orgKey']
);



// WEBPACK FOOTER //
// ./src/store/reducers/dealerSettings/selectors.js